import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo } from "react";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import MyButton from "../../../components/MyButton";
import MyTextField from "../../../components/inputs/MyTextField";
import MyTextArea from "../../../components/inputs/MyTextArea";
import MyInputFile from "../../../components/inputs/MyInputFile";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { SiteContext } from "../../../context/SiteProvider";
import { useFile } from "../../../hooks/useFile";
import { doUploadFiles } from "../../../utils/file";
import { AuthContext } from "../../../context/AuthProvider";
import MyInputCaptcha from "../../../components/inputs/MyInputCaptcha";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const ResearchWrite03 = ({ boardCode, parent }) => {
  const { itemNo } = useParams();
  const {
    createBoardItemMutation,
    updateBoardItemMutation,
    useGetBoardItem,
    useGetBoard,
  } = useBoardItem();
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, isAuthenticated, roleCode, isAdmin, isProfileLoading } =
    useContext(AuthContext);
  const { getFileUploadUrl } = useFile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board } = useGetBoard({ boardCode }, (data) => data.data);

  const { data, isLoading } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      enabled:
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.READ,
          nonPermissions,
          isAdmin
        ) &&
        checkRoleAndPermissions(
          roleCode,
          board,
          BOARD_PERMISSION.WRITE,
          nonPermissions,
          isAdmin
        ),
    },
    (data) => data.data
  );

  const initValues = {
    additionalItems: {
      status: "waiting",
    },
    email: "",
    title: "",
    body: "",
    files: [],
    captchaToken: "",
    isRequiredCaptcha: false,
  };

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("필수 입력 항목입니다."),
    title: yup.string().required("필수 입력 항목입니다."),
    files: yup
      .array()
      .of(yup.mixed().required("파일을 선택해 주세요."))
      .max(5, "최대 5개의 파일만 업로드할 수 있습니다.")
      .nullable(),
    captcha: yup.string().when("isRequiredCaptcha", {
      is: true,
      then: () =>
        yup.string().required("자동입력방지 인증코드를 입력해주세요."),
    }),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const showCaptcha = useMemo(
    () => !!board?.options?.useCaptchaInPost,
    [board]
  );

  const onSubmit = async (vals) => {
    if (
      checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.WRITE,
        nonPermissions,
        isAdmin
      )
    ) {
      const payload = {
        title: vals.title,
        body: vals.body,
        additionalItems: {
          status: vals?.additionalItems?.status ?? "waiting",
        },
        writerEmail: vals.email,
        userId: data?.writer?.userId ? data.writer.userId : profile.userId,
      };

      if (showCaptcha) {
        if (!vals.captchaToken) {
          methods.setError("captcha", {
            message: "인증을 진행해주세요.",
          });
          return;
        }
        payload.captchaToken = vals.captchaToken;
      }

      setLoadingScreen(true);
      let uploadedFiles = [];
      const files = [];

      if (vals.files?.length) {
        vals.files.forEach((file) => {
          files.push(file);
        });
      }

      if (files.length) {
        uploadedFiles = await doUploadFiles(files, getFileUploadUrl, {
          boardCode: board?.boardCode,
        });
      }

      payload.attachedFiles = uploadedFiles.map((file) => ({
        fileName: file.name,
        fileId: file.fileId,
      }));

      if (!itemNo) {
        payload.additionalItems.status = "waiting";

        createBoardItemMutation.mutate(
          { boardCode: board?.boardCode, payload },
          {
            onSuccess: (res) => {
              if (res) {
                methods.reset();
                methods.setValue("email", profile?.email);
                alert("게시글 작성이 완료되었습니다.");
                navigate("/Research/Idea");
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      } else {
        updateBoardItemMutation.mutate(
          {
            boardCode: board?.boardCode,
            itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("게시물이 성공적으로 업데이트되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item/{boardCode}/{itemNo}"],
                });
                navigate(parent ? `${parent}/${itemNo}` : "");
              } else {
                alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("게시물 작성 또는 수정 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      }
    } else {
      alert(getPermissionMessage(BOARD_PERMISSION.WRITE));
    }
  };

  useEffect(() => {
    methods.setValue("isRequiredCaptcha", showCaptcha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptcha]);

  useEffect(() => {
    if (!isLoading && data) {
      methods.setValue("email", data.writer?.email);
      methods.setValue("title", data.title);
      methods.setValue("body", data.body);
      methods.setValue(
        "additionalItems.status",
        data.additionalItems?.status ?? "waiting"
      );

      if (data.attachedFiles?.length) {
        const newFiles = data.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file,
        }));

        methods.setValue("files", newFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  useEffect(() => {
    if (profile?.email && !data?.writer?.email) {
      methods.setValue("email", profile.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return !isProfileLoading ? (
    <div className="border-t-2 border-primary md:pt-10 pt-5">
      <div className="flex items-end flex-wrap md:gap-4 gap-2 md:mb-10 mb-6">
        <div className="mr-auto">
          <h2 className="font-semibold xl:text-[32px] md:text-[28px] text-[22px] mb-1">
            연구과제 제안하기
          </h2>

          <p className="text-gray-dark xl:text-lg text-base">
            제출하신 연구과제는 지난 제안 내역에서 확인하실 수 있습니다.
          </p>
        </div>
        {isAdmin ? (
          <MyButton
            variant="contained"
            color="primary"
            className="font-semibold min-w-[120px]"
            type="submit"
            onClick={() => navigate("/Research/Idea")}
          >
            전체 제안 내역
          </MyButton>
        ) : (
          <Link
            to="/MyPage/IdeaHistory"
            className="flex items-center gap-2 lg:text-lg text-base text-primary border border-primary md:px-3 px-2 md:py-1 py-[2px] rounded-3xl"
          >
            <FontAwesomeIcon icon={faBars} />
            지난 제안 내역
          </Link>
        )}
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="email"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              이메일 <span className="text-error">*</span>
            </label>
            <div className="flex-1">
              <MyTextField
                id="email"
                name="email"
                disabled
                placeholder="접수자의 이메일을 입력해주세요."
              />
            </div>
          </div>

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="title"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              제목 <span className="text-error">*</span>
            </label>
            <div className="flex-1">
              <MyTextField
                id="title"
                name="title"
                placeholder="제목을 입력해주세요."
              />
            </div>
          </div>

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="body"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              요약
            </label>
            <div className="flex-1">
              <MyTextArea id="body" name="body" rows="8" cols="30" />
            </div>
          </div>

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="files"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              파일 첨부
            </label>
            <div className="flex-1">
              <MyInputFile
                name="files"
                multiple
                placeholder="파일을 첨부해 주세요."
                hint="*파일 첨부는 최대 5개까지 업로드 가능합니다."
              />
            </div>
          </div>

          {showCaptcha && (
            <MyInputCaptcha
              name="captchaToken"
              className={isAuthenticated ? "hidden" : `md:mb-10 mb-5`}
              labelClass="md:w-[150px]"
            />
          )}

          <div className="flex items-center justify-end mt-10 sm:gap-4 gap-2">
            <MyButton
              variant="outlined"
              className="sm:min-w-[120px]"
              onClick={() => {
                if (itemNo) {
                  let backUrl = `/MyPage/IdeaHistory/${itemNo}`;
                  if (isAdmin) {
                    if (location.pathname.startsWith("/Research")) {
                      backUrl = `/Research/Idea/${itemNo}`;
                    } else if (location.pathname.startsWith("/MyPage")) {
                      backUrl = `/MyPage/IdeaHistory/${itemNo}`;
                    }
                  }
                  navigate(backUrl);
                } else {
                  methods.reset();
                  methods.setValue("email", profile?.email);
                }
              }}
            >
              <span className="font-semibold">취소</span>
            </MyButton>
            {!itemNo ||
            (!!itemNo &&
              isAuthenticated &&
              (profile?.userId === data?.writer?.userId || isAdmin)) ? (
              <MyButton
                variant="contained"
                color="primary"
                className="font-semibold min-w-[120px]"
                type="submit"
              >
                제출하기
              </MyButton>
            ) : null}
          </div>
        </form>
      </FormProvider>
    </div>
  ) : null;
};

export default ResearchWrite03;
